import { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Globe } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { supportedLanguages, languageLabels, languageFlags } from "@/i18n/config";
import logo from "@/assets/logo.png";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { t } = useTranslation();
  const { currentLang, changeLanguage, localePath } = useLanguage();
  const navigate = useNavigate();

  const navLinks = [
    { label: t("nav.home"), href: localePath("/") + "#hero", isHash: true },
    { label: t("nav.about"), href: localePath("/") + "#about", isHash: true },
    { label: t("nav.products"), href: localePath("/produkty"), isHash: false },
    { label: t("nav.gallery"), href: localePath("/galerie"), isHash: false },
    { label: t("nav.contact"), href: localePath("/") + "#contact", isHash: true },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to={localePath("/")} className="flex items-center">
            <img src={logo} alt="EkoKompakt" className="h-8 md:h-9 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.isHash ? (
                <button key={link.label} onClick={() => navigate(link.href)} className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                  {link.label}
                </button>
              ) : (
                <Link key={link.label} to={link.href} className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* Phone + Lang + CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:+420724301099" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">+420 724 301 099</span>
            </a>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary transition-colors text-sm"
              >
                <Globe className="w-4 h-4" />
                <span>{languageFlags[currentLang]}</span>
              </button>
              <AnimatePresence>
                {langOpen && (
                  <m.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    className="absolute right-0 top-full mt-1 glass rounded-lg border border-border py-1 min-w-[140px] z-50"
                  >
                    {supportedLanguages.map((lng) => (
                      <button
                        key={lng}
                        onClick={() => { changeLanguage(lng); setLangOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-secondary transition-colors ${
                          currentLang === lng ? "text-primary font-medium" : "text-muted-foreground"
                        }`}
                      >
                        <span>{languageFlags[lng]}</span>
                        <span>{languageLabels[lng]}</span>
                      </button>
                    ))}
                  </m.div>
                )}
              </AnimatePresence>
            </div>

            <Button variant="hero" size="sm" onClick={() => navigate(localePath("/") + "#contact")}>
              {t("nav.contactUs")}
            </Button>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden p-2 text-foreground" aria-label="Toggle menu">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <m.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="md:hidden glass border-t border-border">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) =>
                link.isHash ? (
                  <button key={link.label} onClick={() => { navigate(link.href); setIsOpen(false); }} className="py-3 px-4 text-foreground hover:bg-secondary rounded-lg transition-colors text-left">
                    {link.label}
                  </button>
                ) : (
                  <Link key={link.label} to={link.href} onClick={() => setIsOpen(false)} className="py-3 px-4 text-foreground hover:bg-secondary rounded-lg transition-colors">
                    {link.label}
                  </Link>
                )
              )}
              {/* Mobile Language Switcher */}
              <div className="pt-2 border-t border-border mt-2">
                <div className="flex gap-2 px-4 py-2">
                  {supportedLanguages.map((lng) => (
                    <button
                      key={lng}
                      onClick={() => { changeLanguage(lng); setIsOpen(false); }}
                      className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
                        currentLang === lng ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:bg-secondary"
                      }`}
                    >
                      <span>{languageFlags[lng]}</span>
                      <span>{languageLabels[lng]}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-2 border-t border-border">
                <a href="tel:+420724301099" className="flex items-center gap-2 py-3 px-4 text-primary">
                  <Phone className="w-4 h-4" />
                  <span className="font-medium">+420 724 301 099</span>
                </a>
              </div>
            </nav>
          </m.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
